import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import {
  Eye,
  EyeOff,
  Key,
  Lock,
  Loader2,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { usePageTitle } from '../hooks/usePageTitle';
import AuthService from '../services/authService';

// パスワード強度評価型定義（Register.tsxと同じ）
interface PasswordStrength {
  score: number;
  label: string;
  color: 'error' | 'warning' | 'info' | 'success';
  requirements: {
    length: boolean;
    uppercase: boolean;
    lowercase: boolean;
    number: boolean;
    symbol: boolean;
  };
}

/**
 * Password Reset Confirm Page - World-Class UI Implementation
 *
 * Features:
 * - 1-column centered layout (mobile-first design)
 * - Token validation from URL params
 * - Password strength indicator (Register.tsx compatible)
 * - Material-UI Design System v2.0
 * - Responsive design
 * - WCAG 2.1 AA compliant
 * - Smooth micro-interactions
 *
 * @returns Password reset confirm page component
 */
const PasswordResetConfirm: React.FC = () => {
  usePageTitle('新しいパスワードを設定');
  const navigate = useNavigate();
  const { token } = useParams<{ token: string }>();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tokenError, setTokenError] = useState(false);

  // パスワード強度評価（Register.tsxと同じロジック）
  const evaluatePasswordStrength = (password: string): PasswordStrength => {
    const requirements = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /\d/.test(password),
      symbol: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    };

    const score = Object.values(requirements).filter(Boolean).length;

    let label: string;
    let color: 'error' | 'warning' | 'info' | 'success';

    if (score <= 2) {
      label = '弱い';
      color = 'error';
    } else if (score <= 3) {
      label = '普通';
      color = 'warning';
    } else if (score <= 4) {
      label = '強い';
      color = 'info';
    } else {
      label = '非常に強い';
      color = 'success';
    }

    return { score, label, color, requirements };
  };

  const passwordStrength = evaluatePasswordStrength(password);

  // Token validation on mount
  useEffect(() => {
    if (!token) {
      setTokenError(true);
      setError('無効なリセットリンクです。もう一度パスワードリセットをお試しください。');
    }
  }, [token]);

  const validateForm = (): string | null => {
    if (!password.trim()) {
      return '新しいパスワードを入力してください';
    }

    if (password.length < 8) {
      return 'パスワードは8文字以上で入力してください';
    }

    if (!confirmPassword.trim()) {
      return 'パスワード（確認）を入力してください';
    }

    if (password !== confirmPassword) {
      return 'パスワードが一致しません';
    }

    if (passwordStrength.score < 4) {
      return 'より強力なパスワードを設定してください';
    }

    return null;
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    if (!token) {
      setError('トークンが見つかりません');
      return;
    }

    setLoading(true);

    try {
      await AuthService.resetPassword(token, password);
      setLoading(false);
      // Success - redirect to complete page
      navigate('/password-reset/complete');
    } catch (err) {
      setLoading(false);
      const errorMessage = err instanceof Error ? err.message : 'パスワードのリセットに失敗しました。リンクの有効期限が切れている可能性があります。';
      setError(errorMessage);
    }
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4 md:p-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        <div className="max-w-[480px] mx-auto w-full">
          {/* SALONHUB Logo */}
          <img
            src="/assets/logos/salon-hub-logo.png"
            alt="SALON HUB"
            className="w-full max-w-[175px] h-auto mb-6 transition-transform duration-300 cursor-pointer hover:scale-105"
            onClick={handleBackToLogin}
          />

          {/* Title */}
          <h1 className="scroll-m-20 text-2xl font-bold tracking-tight text-gray-900 mb-2 leading-tight">
            新しいパスワードを設定
          </h1>

          <p className="text-base text-gray-600 leading-relaxed mb-6">
            安全な新しいパスワードを作成してください
          </p>

          {/* Token Error Alert */}
          {tokenError && (
            <Alert className="mb-4 border-red-200 bg-red-50">
              <AlertDescription className="text-sm text-red-800">
                無効なリセットリンクです。もう一度パスワードリセットをお試しください。
              </AlertDescription>
            </Alert>
          )}

          {/* Password Reset Form */}
          <form onSubmit={handlePasswordReset}>
            {/* Error Alert */}
            {error && !tokenError && (
              <Alert className="mb-4 border-red-200 bg-red-50">
                <AlertDescription className="text-sm text-red-800">
                  {error}
                </AlertDescription>
              </Alert>
            )}

            {/* New Password Field */}
            <div className="space-y-2 mb-2">
              <Label htmlFor="password">新しいパスワード</Label>
              <div className="relative">
                <Key className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  autoFocus
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (error) setError('');
                  }}
                  onFocus={() => setPasswordFocused(true)}
                  onBlur={() => setPasswordFocused(false)}
                  placeholder="8文字以上で入力"
                  required
                  disabled={tokenError}
                  className="pl-10 pr-10 bg-white"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label="パスワード表示切替"
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 text-gray-400" />
                  ) : (
                    <Eye className="h-4 w-4 text-gray-400" />
                  )}
                </Button>
              </div>
            </div>

            {/* パスワード強度インジケーター */}
            {(password || passwordFocused) && (
              <div className="mb-2">
                <div className="flex items-center mb-1">
                  <p className="text-sm mr-1">パスワード強度:</p>
                  <p className={`text-sm font-semibold ${
                    passwordStrength.color === 'error' ? 'text-red-500' :
                    passwordStrength.color === 'warning' ? 'text-orange-500' :
                    passwordStrength.color === 'info' ? 'text-blue-500' :
                    'text-green-500'
                  }`}>
                    {passwordStrength.label}
                  </p>
                </div>
                <Progress
                  value={(passwordStrength.score / 5) * 100}
                  className={`h-2 mb-1 ${
                    passwordStrength.color === 'error' ? '[&>div]:bg-red-500' :
                    passwordStrength.color === 'warning' ? '[&>div]:bg-orange-500' :
                    passwordStrength.color === 'info' ? '[&>div]:bg-blue-500' :
                    '[&>div]:bg-green-500'
                  }`}
                />
                <div className="text-xs text-gray-600">
                  {Object.entries({
                    length: '8文字以上',
                    uppercase: '大文字',
                    lowercase: '小文字',
                    number: '数字',
                    symbol: '記号',
                  }).map(([key, label]) => (
                    <p
                      key={key}
                      className={`text-xs ${
                        passwordStrength.requirements[key as keyof typeof passwordStrength.requirements]
                          ? 'text-green-500'
                          : 'text-gray-500'
                      }`}
                    >
                      {passwordStrength.requirements[key as keyof typeof passwordStrength.requirements] ? '✓' : '×'} {label}
                    </p>
                  ))}
                </div>
              </div>
            )}

            {/* Confirm Password Field */}
            <div className="space-y-2 mb-6">
              <Label htmlFor="confirmPassword">パスワード（確認）</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    if (error) setError('');
                  }}
                  placeholder="もう一度入力"
                  required
                  disabled={tokenError}
                  className={`pl-10 pr-10 bg-white ${
                    confirmPassword !== '' && password !== confirmPassword ? 'border-red-500' : ''
                  }`}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  aria-label="パスワード表示切替"
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-4 w-4 text-gray-400" />
                  ) : (
                    <Eye className="h-4 w-4 text-gray-400" />
                  )}
                </Button>
              </div>
              {confirmPassword !== '' && password !== confirmPassword && (
                <p className="text-sm text-red-500">パスワードが一致しません</p>
              )}
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              className="w-full min-h-[56px] py-2.5 mb-4"
              disabled={loading || tokenError}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  パスワード変更中...
                </>
              ) : (
                'パスワードを変更'
              )}
            </Button>

            {/* Back to Login */}
            <Button
              type="button"
              variant="outline"
              className="w-full min-h-[56px] py-2.5"
              onClick={handleBackToLogin}
            >
              ログインページに戻る
            </Button>
          </form>
        </div>
      </motion.div>
    </div>
  );
};

export default PasswordResetConfirm;